import React from 'react';
import i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import moment from 'moment';

import { withTheme } from '@material-ui/core/styles';
import { withStyles } from '@material-ui/styles';

import { getFormat, makeGraph } from 'lib/commonLib';
import { timeAmtGraph } from 'lib/api/time';

import CurrentStatus from 'components/atoms/CurrentStatus';
import OutlinedButton from 'components/atoms/OutlinedButton';
import ChartButtonWrap from 'components/molecules/ChartButtonWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

import GraphModal from './GraphModal';

const styles = (theme) => ({
  content: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  marginR0: {
    marginRight: '0 !important',
  },
  graphWrap: {
    width: '100%',
  },
  selectAllBtn: {
    marginLeft: 5,
  },
});

const graphList = [
  {
    title: i18next.t('지출금액대비 세션ROAS'),
    data: { bar: ['EXPT_ADVRTS_AMT', 'ADVRTS_AMT'], line: ['SESSION_ROAS'] },
    yAxisData: {
      left: { EXPT_ADVRTS_AMT: true, ADVRTS_AMT: true },
      right: { SESSION_ROAS: true, isPercent: true },
    },
    customizeData: {
      SESSION_ROAS: {
        isPercent: true,
      },
    },
    referenceLine: {
      SESSION_ROAS: {
        type: 'average',
        label: i18next.t('평균선'),
        visibleValue: true,
      },
    },
  },
  {
    title: i18next.t('지출금액대비 전환율'),
    data: { bar: ['EXPT_ADVRTS_AMT', 'ADVRTS_AMT'], line: ['CONV_RATE'] },
    yAxisData: {
      left: { EXPT_ADVRTS_AMT: true, ADVRTS_AMT: true },
      right: { CONV_RATE: true, isPercent: true },
    },
    customizeData: {
      CONV_RATE: {
        tooltipFormat: '0.00',
        isPercent: true,
        visibleValue: true,
      },
    },
    referenceLine: {
      CONV_RATE: {
        type: 'average',
        label: i18next.t('평균선'),
        visibleValue: true,
      },
    },
  },
  {
    title: i18next.t('노출대비 지출금액'),
    data: { bar: ['EXPT_PAR_EPRS_CNT', 'PAR_EPRS_CNT'], line: ['ADVRTS_AMT'] },
    yAxisData: {
      left: { PAR_EPRS_CNT: true, EXPT_PAR_EPRS_CNT: true },
      right: { ADVRTS_AMT: true },
    },
    referenceLine: {
      ADVRTS_AMT: {
        type: 'average',
        label: i18next.t('평균선'),
        visibleValue: true,
      },
    },
  },
  {
    title: 'CTR',
    data: { bar: ['CLICK_RATE'] },
    yAxisData: {
      left: { CLICK_RATE: true, isPercent: true },
      right: {},
    },
    customizeData: {
      CLICK_RATE: {
        tooltipFormat: '0.000',
        isPercent: true,
      },
    },
    referenceLine: {
      CLICK_RATE: {
        type: 'average',
        label: i18next.t('평균선'),
        visibleValue: true,
      },
    },
  },
  {
    title: 'ECPM',
    data: { bar: ['ECPM'] },
    yAxisData: {
      left: { ECPM: true },
      right: {},
    },
    referenceLine: {
      ECPM: {
        type: 'average',
        label: i18next.t('ECPM'),
        visibleValue: true,
      },
    },
  },
];

class StatisticsGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphData: [],
      infoMessage: '',
      loading: false,
      error: false,
      noData: false,
      isOpen: false,
    };
  }

  getData = () => {
    const { params, locale, t } = this.props;
    const { sDate, eDate } = params;
    const format = getFormat(locale);
    this.setState({
      ...this.state,
      loading: true,
      noData: false,
      error: false,
      infoMessage: t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      }),
    });
    timeAmtGraph(params)
      .then((response) => {
        const { data } = response.data;

        if (data.length === 0) {
          this.setState({
            ...this.state,
            loading: false,
            error: false,
            noData: true,
          });
        } else {
          this.setState({
            ...this.state,
            primitiveData: data,
            loading: false,
            error: false,
            noData: false,
          });
        }

        this.handleChangeGraph(graphList[0], 0)();
      })
      .catch((error) => {
        this.setState({
          ...this.state,
          loading: false,
          error: true,
          noData: false,
        });
      });
  };

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    const { params: prevParams } = prevProps;
    const { params } = this.props;

    if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
      this.getData();
    }
  }

  /**
   * 새로고침
   */
  handleRefesh = () => {
    this.getData();
  };

  // 시간 반환
  makeLabel = (onlyDay) => (originalDate) => {
    const time = originalDate;

    return onlyDay ? `${time}` : <span>{`${time}`}</span>;
  };

  xAxisTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const timeStr = this.makeLabel(true)(value);

    return (
      <text x={x} y={y + 10} textAnchor="middle">
        {timeStr}
      </text>
    );
  };

  /**
   * 버튼 클릭하여 그래프 데이터 변경
   */
  handleChangeGraph =
    (
      {
        title,
        data,
        yAxisData,
        customizeData: customize,
        isPercent = false,
        referenceLine,
      },
      index
    ) =>
    (e) => {
      const { primitiveData } = this.state;
      const { classes } = this.props;

      if (typeof data !== 'undefined') {
        const { graphData, dataKeys, customizeData, keyLabel, referenceData } =
          makeGraph({ data, customize, primitiveData, referenceLine });

        const result = (
          <MultiplexChart
            width="100%"
            xAxisTick={this.xAxisTick}
            makeLabel={this.makeLabel(false)}
            className={`${classes.marginR0} ${classes.graphWrap}`}
            data={graphData}
            dataKeys={dataKeys}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={null}
            yAxisData={yAxisData}
            legend
            title={title}
            isPercent={isPercent}
            key={title}
            referenceLine={referenceData}
          />
        );

        this.setState({
          ...this.state,
          curGraphComponent: result,
          selectedIdx: index,
        });
      }
    };

  handleToggleModal = (flag) => {
    this.setState({
      ...this.state,
      isOpen: flag,
    });
  };

  /**
   * 자세히보기 클릭
   * @param {string} name
   * @param {*} value
   */
  handleOnToggleClick = () => (e) => {
    this.setState({
      ...this.state,
      isOpen: true,
    });
  };

  render() {
    const {
      handleRefesh,
      handleChangeGraph,
      handleOnToggleClick,
      handleToggleModal,
    } = this;
    const { params, classes, t } = this.props;

    const {
      infoMessage,
      curGraphComponent,
      selectedIdx,
      primitiveData,
      loading,
      error,
      noData,
      isOpen,
    } = this.state;
    return (
      <SwitchingArea
        title={
          <>
            <InfoTitleArea
              title={t('시간대별 그래프')}
              tooltipTitle={infoMessage}
              iconType="fainfoCircle"
              iconColor="graph"
            />

            <OutlinedButton
              index={0}
              className={classes.selectAllBtn}
              onClick={handleOnToggleClick()}
            >
              {t('자세히보기')}
            </OutlinedButton>
          </>
        }
        handleRefesh={handleRefesh}
      >
        {loading || noData || error ? (
          <CurrentStatus
            type={loading ? 'loading' : noData ? 'noData' : error && 'error'}
          />
        ) : (
          <>
            <ChartButtonWrap>
              {graphList.map((item, index) => {
                return (
                  <OutlinedButton
                    key={item.title}
                    onClick={handleChangeGraph(item, index)}
                    selected={selectedIdx === index}
                    disabled={!Boolean(primitiveData)}
                  >
                    {item.title}
                  </OutlinedButton>
                );
              })}
            </ChartButtonWrap>
            <div className={classes.content}>{curGraphComponent}</div>
          </>
        )}
        <GraphModal
          isOpen={isOpen}
          params={{ ...params }}
          handleToggleModal={handleToggleModal}
        ></GraphModal>
      </SwitchingArea>
    );
  }
}

export default withTranslation()(
  connect(
    ({ locale }) => ({
      locale: locale.locale,
    }),
    null
  )(withStyles(styles)(withTheme(StatisticsGraph)))
);
